<script setup>
/* global axios */
// Contato (vCard) do WhatsApp, encaminhado pelo AstraCall como texto:
//   👤 *Contato: <nome>*
//   📞 <telefone>[\n📞 <telefone2>...]
// (ou "👤 *N contatos compartilhados*\n• Nome — telefone" p/ vários)
// Card estilo WhatsApp: avatar + nome + botões "Conversar" (cria contato + abre
// conversa) e "{{ t('CONVERSATION.CONTACT_SHARE.SHOW_COMPANY') }}" (modal c/ foto/nome/número).
// Detecção/roteamento no Message.vue (marcador 👤).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMessageContext } from '../provider.js';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { content, inboxId } = useMessageContext();
const route = useRoute();
const router = useRouter();

const detailsDialog = ref(null);
const starting = ref('');
const errorMsg = ref('');

const data = computed(() => {
  const raw = String(content?.value || '');
  const lines = raw.split('\n');

  // vários contatos: "👤 *N contatos compartilhados*"
  if (/contatos compartilhados/i.test(raw)) {
    const list = lines
      .map(l => l.match(/^\s*•\s*(.+?)(?:\s+—\s+(.+))?$/))
      .filter(Boolean)
      .map(m => ({ name: m[1].trim(), phone: (m[2] || '').trim() }));
    return { multi: true, list };
  }

  const name =
    (raw.match(/👤\s*\*Contato:\s*([^*]+)\*/) || [])[1]?.trim() || 'Contato';
  const phones = lines
    .map(l => (l.match(/📞\s*(.+)/) || [])[1]?.trim())
    .filter(Boolean);
  return { multi: false, name, phones, phone: phones[0] || '' };
});

const initials = computed(() => {
  const words = String(data.value.name || '')
    .split(/\s+/)
    .filter(w => /[a-zA-ZÀ-ÿ0-9]/.test(w));
  return (
    words
      .slice(0, 2)
      .map(w => w[0])
      .join('') || '?'
  ).toUpperCase();
});

const startConversation = async (phone, name) => {
  if (starting.value) return;
  errorMsg.value = '';
  starting.value = phone;
  try {
    const base = `/api/v1/accounts/${route.params.accountId}/kanban/connections`;
    const res = await axios.post(`${base}/start_conversation`, {
      inbox_id: inboxId?.value,
      phone,
      name,
    });
    const convId = res.data?.data?.conversation_id;
    if (convId) {
      router.push(
        `/app/accounts/${route.params.accountId}/conversations/${convId}`
      );
    }
  } catch (e) {
    errorMsg.value = e?.response?.data?.error || 'Falha ao abrir a conversa';
  } finally {
    starting.value = '';
  }
};
</script>

<template>
  <!-- vários contatos: lista compacta -->
  <BaseBubble
    v-if="data.multi"
    class="!p-3.5 !max-w-[320px] min-w-[260px]"
    data-bubble-name="contact-share"
    hide-meta
  >
    <div class="flex items-center gap-1.5 mb-2 text-n-slate-11">
      <span class="i-ph-users-three-bold size-4" />
      <span class="text-xs font-medium tracking-wide uppercase">{{
        t('CONVERSATION.CONTACT_SHARE.HEADING_PLURAL')
      }}</span>
    </div>
    <div class="flex flex-col divide-y divide-n-weak">
      <div
        v-for="(c, i) in data.list"
        :key="i"
        class="flex items-center justify-between gap-2 py-2"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">{{
            c.name
          }}</span>
          <span v-if="c.phone" class="text-xs text-n-slate-11">{{
            c.phone
          }}</span>
        </div>
        <button
          v-if="c.phone"
          type="button"
          class="text-xs font-medium shrink-0 text-n-brand hover:underline disabled:opacity-50"
          :disabled="starting === c.phone"
          @click="startConversation(c.phone, c.name)"
        >
          {{ t('CONVERSATION.CONTACT_SHARE.CHAT') }}
        </button>
      </div>
    </div>
    <p v-if="errorMsg" class="mt-2 text-xs text-n-ruby-11">{{ errorMsg }}</p>
  </BaseBubble>

  <!-- contato único: card estilo WhatsApp -->
  <BaseBubble
    v-else
    class="!p-0 overflow-hidden !max-w-[320px] min-w-[270px]"
    data-bubble-name="contact-share"
    hide-meta
  >
    <div class="flex items-center gap-3 p-3.5">
      <div
        class="flex items-center justify-center text-sm font-semibold text-white rounded-full size-11 shrink-0 bg-n-brand"
      >
        {{ initials }}
      </div>
      <div class="flex flex-col min-w-0">
        <span
          class="text-sm font-semibold leading-tight truncate text-n-slate-12"
        >
          {{ data.name }}
        </span>
        <span v-if="data.phone" class="text-xs truncate text-n-slate-11">
          {{ data.phone }}
        </span>
      </div>
    </div>

    <div class="flex border-t border-n-weak">
      <button
        type="button"
        class="flex items-center justify-center flex-1 gap-1.5 py-2.5 text-sm font-medium whitespace-nowrap text-n-brand hover:bg-n-alpha-1 disabled:opacity-60"
        :disabled="!data.phone || !!starting"
        @click="startConversation(data.phone, data.name)"
      >
        <span
          :class="
            starting ? 'i-ph-circle-notch animate-spin' : 'i-ph-chat-circle'
          "
          class="size-4"
        />
        {{ t('CONVERSATION.CONTACT_SHARE.CHAT') }}
      </button>
      <button
        type="button"
        class="flex items-center justify-center flex-1 gap-1.5 py-2.5 text-sm font-medium whitespace-nowrap border-l border-n-weak text-n-brand hover:bg-n-alpha-1"
        @click="detailsDialog?.open()"
      >
        <span class="i-ph-identification-card size-4" />
        {{ t('CONVERSATION.CONTACT_SHARE.SHOW_COMPANY') }}
      </button>
    </div>

    <p v-if="errorMsg" class="px-3.5 py-2 text-xs text-n-ruby-11">
      {{ errorMsg }}
    </p>

    <!-- modal "{{ t('CONVERSATION.CONTACT_SHARE.SHOW_COMPANY') }}": foto/nome/número -->
    <Dialog
      ref="detailsDialog"
      width="sm"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex items-center justify-between -mt-2">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CONVERSATION.CONTACT_SHARE.HEADING') }}
        </h3>
        <button
          type="button"
          class="flex text-n-slate-11 hover:text-n-slate-12"
          @click="detailsDialog?.close()"
        >
          <span class="i-ph-x-bold size-4" />
        </button>
      </div>

      <div class="flex flex-col items-center gap-3 py-2">
        <div
          class="flex items-center justify-center text-2xl font-semibold text-white rounded-full size-20 bg-n-brand"
        >
          {{ initials }}
        </div>
        <span class="text-lg font-semibold text-center text-n-slate-12">
          {{ data.name }}
        </span>
      </div>

      <div class="flex flex-col divide-y divide-n-weak">
        <div
          v-for="(ph, i) in data.phones"
          :key="i"
          class="flex items-center gap-3 py-3"
        >
          <span class="i-ph-phone size-5 shrink-0 text-n-slate-10" />
          <div class="flex flex-col">
            <span class="text-sm text-n-slate-12">{{ ph }}</span>
            <span class="text-xs text-n-slate-10">{{
              t('CONVERSATION.CONTACT_SHARE.PHONE')
            }}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="flex items-center justify-center w-full gap-2 py-2.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
        :disabled="!data.phone || !!starting"
        @click="startConversation(data.phone, data.name)"
      >
        <span class="i-ph-chat-circle size-4" />
        {{ t('CONVERSATION.CONTACT_SHARE.CHAT') }}
      </button>
    </Dialog>
  </BaseBubble>
</template>
